/**
 * Enhanced Evaluation Storage Utilities
 * Robust local storage system for interview evaluation and transcript data
 * Primary storage: localStorage + file downloads
 * Secondary storage: AWS/GraphQL (with fallback handling)
 */

// Get all stored evaluation data for an interview slot (enhanced version)
export const getStoredEvaluationData = (interviewSlotId) => {
  try {
    // Try enhanced keys first
    let transcriptData = localStorage.getItem(`interview_transcript_${interviewSlotId}`);
    let evaluationData = localStorage.getItem(`interview_evaluation_${interviewSlotId}`);
    let awsErrorData = localStorage.getItem(`aws_error_${interviewSlotId}`);
    
    // Fallback to legacy keys for backward compatibility
    if (!transcriptData) {
      transcriptData = localStorage.getItem(`transcript_${interviewSlotId}`);
    }
    if (!evaluationData) {
      evaluationData = localStorage.getItem(`evaluation_${interviewSlotId}`);
    }
    
    return {
      transcript: transcriptData ? JSON.parse(transcriptData) : null,
      evaluation: evaluationData ? JSON.parse(evaluationData) : null,
      awsError: awsErrorData ? JSON.parse(awsErrorData) : null,
      hasData: !!(transcriptData || evaluationData),
      storage_method: transcriptData?.includes('enhanced_local') ? 'enhanced' : 'legacy'
    };
  } catch (error) {
    console.error('Error retrieving stored evaluation data:', error);
    return { hasData: false, error: error.message };
  }
};

// Get all stored interviews (enhanced to handle both legacy and new storage keys)
export const getAllStoredInterviews = () => {
  const interviews = {};
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      
      // Handle both legacy and enhanced storage keys
      if (key.startsWith('transcript_') || key.startsWith('evaluation_') || key.startsWith('aws_error_') ||
          key.startsWith('interview_transcript_') || key.startsWith('interview_evaluation_')) {
        
        let type, slotId;
        
        // Parse enhanced keys
        if (key.startsWith('interview_transcript_')) {
          type = 'transcript';
          slotId = key.replace('interview_transcript_', '');
        } else if (key.startsWith('interview_evaluation_')) {
          type = 'evaluation';
          slotId = key.replace('interview_evaluation_', '');
        } else {
          // Parse legacy keys
          [type, slotId] = key.split('_', 2);
        }
        
        if (!interviews[slotId]) {
          interviews[slotId] = {
            slotId,
            transcript: null,
            evaluation: null,
            awsError: null,
            storage_method: key.startsWith('interview_') ? 'enhanced' : 'legacy'
          };
        }
        
        try {
          const data = JSON.parse(localStorage.getItem(key));
          interviews[slotId][type] = data;
        } catch (parseError) {
          console.warn(`Failed to parse stored data for key: ${key}`, parseError);
        }
      }
    }
    
    return interviews;
  } catch (error) {
    console.error('Error scanning stored interviews:', error);
    return {};
  }
};

// Export interview data as downloadable files
export const exportInterviewData = (interviewSlotId) => {
  const storedData = getStoredEvaluationData(interviewSlotId);
  
  if (!storedData.hasData) {
    console.warn('No stored data found for interview slot:', interviewSlotId);
    return false;
  }
  
  try {
    const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const timeStr = new Date().toTimeString().split(' ')[0].replace(/:/g, '');
    
    // Export transcript
    if (storedData.transcript) {
      const transcriptBlob = new Blob([JSON.stringify(storedData.transcript, null, 2)], { 
        type: 'application/json' 
      });
      downloadFile(transcriptBlob, `transcript_${interviewSlotId}_${timestamp}_${timeStr}.json`);
    }
    
    // Export evaluation
    if (storedData.evaluation) {
      const evaluationBlob = new Blob([JSON.stringify(storedData.evaluation, null, 2)], { 
        type: 'application/json' 
      });
      downloadFile(evaluationBlob, `evaluation_${interviewSlotId}_${timestamp}_${timeStr}.json`);
    }
    
    // Export combined data
    const combinedData = {
      interview_slot_id: interviewSlotId,
      export_timestamp: new Date().toISOString(),
      data: storedData
    };
    
    const combinedBlob = new Blob([JSON.stringify(combinedData, null, 2)], { 
      type: 'application/json' 
    });
    downloadFile(combinedBlob, `interview_complete_${interviewSlotId}_${timestamp}_${timeStr}.json`);
    
    console.log('✅ Interview data exported successfully');
    return true;
    
  } catch (error) {
    console.error('Error exporting interview data:', error);
    return false;
  }
};

// Helper function to trigger file download
const downloadFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Clear stored data for an interview slot
export const clearStoredInterviewData = (interviewSlotId) => {
  try {
    localStorage.removeItem(`transcript_${interviewSlotId}`);
    localStorage.removeItem(`evaluation_${interviewSlotId}`);
    localStorage.removeItem(`aws_error_${interviewSlotId}`);
    
    console.log('🗑️ Cleared stored data for interview slot:', interviewSlotId);
    return true;
  } catch (error) {
    console.error('Error clearing stored interview data:', error);
    return false;
  }
};

// Generate summary report of stored data
export const getStorageReport = () => {
  const interviews = getAllStoredInterviews();
  const slotIds = Object.keys(interviews);
  
  return {
    total_interviews: slotIds.length,
    interviews_with_transcripts: slotIds.filter(id => interviews[id].transcript).length,
    interviews_with_evaluations: slotIds.filter(id => interviews[id].evaluation).length,
    interviews_with_aws_errors: slotIds.filter(id => interviews[id].awsError).length,
    oldest_interview: slotIds.length > 0 ? Math.min(...slotIds.map(id => 
      interviews[id].evaluation?.created_at || interviews[id].transcript?.created_at || Date.now()
    )) : null,
    latest_interview: slotIds.length > 0 ? Math.max(...slotIds.map(id => 
      interviews[id].evaluation?.created_at || interviews[id].transcript?.created_at || 0
    )) : null,
    storage_size_kb: Math.round(JSON.stringify(interviews).length / 1024),
    interviews
  };
};

// Utility to format transcript for display
export const formatTranscriptForDisplay = (transcript) => {
  if (!transcript) return 'No transcript available';
  
  if (typeof transcript === 'string') {
    return transcript;
  }
  
  if (Array.isArray(transcript)) {
    return transcript.map(entry => {
      if (typeof entry === 'object' && entry.speaker && entry.message) {
        const timestamp = entry.timestamp ? `[${entry.timestamp}] ` : '';
        return `${timestamp}${entry.speaker}: ${entry.message}`;
      }
      return String(entry);
    }).join('\n');
  }
  
  return JSON.stringify(transcript, null, 2);
};

// Utility to validate stored evaluation data
export const validateStoredEvaluation = (evaluationData) => {
  if (!evaluationData) return { valid: false, errors: ['No evaluation data provided'] };
  
  const errors = [];
  const warnings = [];
  
  // Check required fields
  if (!evaluationData.interview_slot_id) errors.push('Missing interview_slot_id');
  if (!evaluationData.candidate_email) errors.push('Missing candidate_email');
  if (!evaluationData.created_at) errors.push('Missing created_at timestamp');
  
  // Check evaluation content
  if (typeof evaluationData.overall_score !== 'number') {
    warnings.push('Overall score is not a number');
  }
  
  if (!evaluationData.recommendation) {
    warnings.push('Missing recommendation');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    score: errors.length === 0 ? (warnings.length === 0 ? 'excellent' : 'good') : 'needs_attention'
  };
};

// Enhanced storage function with structured file organization
export const storeInterviewDataLocally = async (evaluationData, candidateData, jobData, candidateEmail, messages = []) => {
  try {
    console.log('🏪 Enhanced local storage - storing evaluation data');
    
    // Create consistent identifiers
    const timestamp = new Date().toISOString();
    const dateStr = timestamp.split('T')[0].replace(/-/g, '');
    const timeStr = timestamp.split('T')[1].split('.')[0].replace(/:/g, '');
    const interviewSlotId = evaluationData.interviewSlotId || evaluationData.interview_slot_id || `temp_${Date.now()}`;
    const candidateNameSafe = (candidateData?.name || candidateEmail?.split('@')[0] || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
    
    // 1. PROCESS TRANSCRIPT DATA
    let transcriptData = [];
    
    if (evaluationData.transcript && Array.isArray(evaluationData.transcript)) {
      transcriptData = evaluationData.transcript;
    } else if (messages && messages.length > 0) {
      transcriptData = messages.map((msg, index) => ({
        timestamp: msg.timestamp || `${Math.floor(index * 30)}s`,
        speaker: msg.type === 'ai' ? 'interviewer' : 'candidate',
        message: msg.text || '',
        sequence: index + 1
      }));
    }

    // 2. PROCESS EVALUATION DATA
    let evaluationInfo = {};
    
    if (evaluationData.evaluation) {
      evaluationInfo = evaluationData.evaluation;
    } else {
      evaluationInfo = {
        overall_score: evaluationData.overall_score || 0,
        technical_skills: evaluationData.technical_skills || 0,
        communication_skills: evaluationData.communication_skills || 0,
        problem_solving: evaluationData.problem_solving || 0,
        candidate_strengths: evaluationData.candidate_strengths || [],
        areas_for_improvement: evaluationData.areas_for_improvement || [],
        recommendation: evaluationData.recommendation || 'PENDING',
        detailed_feedback: evaluationData.detailed_feedback || ''
      };
    }

    // 3. CREATE STRUCTURED FILE DATA
    const transcriptFileData = {
      metadata: {
        interview_slot_id: interviewSlotId,
        candidate_name: candidateData?.name || 'Unknown Candidate',
        candidate_email: candidateEmail || 'unknown@example.com',
        job_title: jobData?.title || 'Unknown Position',
        company_name: jobData?.company_name || 'Unknown Company',
        interview_date: dateStr,
        interview_time: timeStr,
        duration_minutes: evaluationData.interview_summary?.duration_minutes || 0,
        total_questions: evaluationData.interview_summary?.questions_asked || 0,
        created_at: timestamp,
        storage_location: `interview/transcripts/transcript_${candidateNameSafe}_${dateStr}_${timeStr}.json`,
        storage_method: 'enhanced_local'
      },
      transcript: transcriptData,
      interview_summary: evaluationData.interview_summary || {}
    };

    const evaluationFileData = {
      metadata: {
        interview_slot_id: interviewSlotId,
        candidate_name: candidateData?.name || 'Unknown Candidate',
        candidate_email: candidateEmail || 'unknown@example.com',
        job_title: jobData?.title || 'Unknown Position',
        company_name: jobData?.company_name || 'Unknown Company',
        interview_date: dateStr,
        interview_time: timeStr,
        created_at: timestamp,
        storage_location: `interview/evaluations/evaluation_${candidateNameSafe}_${dateStr}_${timeStr}.json`,
        storage_method: 'enhanced_local'
      },
      evaluation: evaluationInfo,
      interview_summary: evaluationData.interview_summary || {},
      raw_bot_response: evaluationData
    };

    // 4. STORE IN LOCALSTORAGE WITH ENHANCED KEYS
    const transcriptStorageKey = `interview_transcript_${interviewSlotId}`;
    const evaluationStorageKey = `interview_evaluation_${interviewSlotId}`;
    
    localStorage.setItem(transcriptStorageKey, JSON.stringify(transcriptFileData));
    localStorage.setItem(evaluationStorageKey, JSON.stringify(evaluationFileData));
    
    // Also store with legacy keys for backward compatibility
    localStorage.setItem(`transcript_${interviewSlotId}`, JSON.stringify(transcriptFileData));
    localStorage.setItem(`evaluation_${interviewSlotId}`, JSON.stringify(evaluationFileData));

    console.log('💾 Data stored in localStorage with keys:', {
      transcript: transcriptStorageKey,
      evaluation: evaluationStorageKey
    });

    // 5. DOWNLOAD FILES WITH ORGANIZED STRUCTURE
    const transcriptFileName = `transcript_${candidateNameSafe}_${dateStr}_${timeStr}.json`;
    const evaluationFileName = `evaluation_${candidateNameSafe}_${dateStr}_${timeStr}.json`;
    
    await downloadFileWithStructure(transcriptFileData, transcriptFileName, 'transcripts');
    await downloadFileWithStructure(evaluationFileData, evaluationFileName, 'evaluations');

    console.log('📁 Files organized in structure:');
    console.log('  📂 interview/');
    console.log('    📂 transcripts/');
    console.log(`      📄 ${transcriptFileName}`);
    console.log('    📂 evaluations/');
    console.log(`      📄 ${evaluationFileName}`);

    return {
      success: true,
      storage_method: 'enhanced_local',
      transcript_stored: true,
      evaluation_stored: true,
      transcript_key: transcriptStorageKey,
      evaluation_key: evaluationStorageKey,
      files_downloaded: true,
      interview_slot_id: interviewSlotId
    };

  } catch (error) {
    console.error('❌ Enhanced local storage failed:', error);
    throw error;
  }
};

// Enhanced file download with better organization
const downloadFileWithStructure = async (data, filename, subfolder) => {
  try {
    const blob = new Blob([JSON.stringify(data, null, 2)], { 
      type: 'application/json' 
    });
    
    // Create download with suggested folder structure in filename
    const structuredFilename = `interview_${subfolder}_${filename}`;
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = structuredFilename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up after download
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    
    console.log(`📥 Downloaded: ${structuredFilename}`);
    return true;
    
  } catch (error) {
    console.warn('Download failed for:', filename, error);
    return false;
  }
};

// Enhanced AWS storage attempt with better error handling
export const attemptAWSStorage = async (evaluationData, candidateEmail, interviewSlotId, createInterviewEvaluation, API) => {
  try {
    console.log('📤 Attempting AWS storage...');
    
    // Extract transcript data for AWS (simplified format)
    let transcriptForAws = '';
    
    if (evaluationData.transcript && Array.isArray(evaluationData.transcript)) {
      transcriptForAws = evaluationData.transcript.map(entry => {
        if (typeof entry === 'object' && entry.speaker && entry.message) {
          return `${entry.speaker}: ${entry.message}`;
        }
        return String(entry);
      }).join('\n');
    }
    
    // Limit transcript size for AWS storage
    const maxLength = 2500;
    if (transcriptForAws.length > maxLength) {
      transcriptForAws = transcriptForAws.substring(0, maxLength) + '\n[...truncated for AWS storage...]';
    }
    
    // Create AWS-compatible evaluation input
    const awsEvaluationInput = {
      interviewSlotID: interviewSlotId,
      roundNumber: 1,
      reappearRoundNumber: 0,
      transcript: transcriptForAws || 'No transcript available',
      evaluation: JSON.stringify({
        overall_score: evaluationData.overall_score || 0,
        technical_skills: evaluationData.technical_skills || 0,
        communication_skills: evaluationData.communication_skills || 0,
        problem_solving: evaluationData.problem_solving || 0,
        recommendation: evaluationData.recommendation || 'PENDING',
        candidate_strengths: evaluationData.candidate_strengths || [],
        areas_for_improvement: evaluationData.areas_for_improvement || []
      }),
      candidateEmail: candidateEmail || 'unknown@example.com',
      feedbackForAI: evaluationData.detailed_feedback || 'Evaluation completed via enhanced local storage',
      videoURL: ''
    };

    console.log('AWS storage attempt with data:', {
      interviewSlotID: awsEvaluationInput.interviewSlotID,
      candidateEmail: awsEvaluationInput.candidateEmail,
      transcriptLength: awsEvaluationInput.transcript.length
    });

    const awsResult = await API.graphql({
      query: createInterviewEvaluation,
      variables: { input: awsEvaluationInput }
    });

    if (awsResult.data?.createInterviewEvaluation) {
      console.log('✅ AWS storage successful:', awsResult.data.createInterviewEvaluation.id);
      return {
        success: true,
        aws_record_id: awsResult.data.createInterviewEvaluation.id,
        aws_stored_at: new Date().toISOString()
      };
    } else {
      console.warn('⚠️ AWS returned no data');
      return { success: false, error: 'No data returned from AWS' };
    }

  } catch (awsError) {
    console.warn('⚠️ AWS storage failed:', awsError.message);
    
    // Store AWS error for debugging
    const errorKey = `aws_error_${interviewSlotId}`;
    const errorData = {
      timestamp: new Date().toISOString(),
      error: awsError.message,
      error_details: awsError.errors || [],
      attempted_data: {
        interviewSlotID: interviewSlotId,
        candidateEmail: candidateEmail
      }
    };
    
    localStorage.setItem(errorKey, JSON.stringify(errorData));
    
    return { 
      success: false, 
      error: awsError.message,
      error_details: awsError.errors 
    };
  }
};
